// components/IOSInstallOverlay.tsx
import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FaShare, FaTimes, FaPlus } from 'react-icons/fa';

interface IOSInstallOverlayProps {
  isVisible: boolean;
  onClose: () => void;
}

export const IOSInstallOverlay = ({ isVisible, onClose }: IOSInstallOverlayProps) => {
  return (
    <AnimatePresence>
      {isVisible && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50"
            onClick={onClose}
          />
          
          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            transition={{ type: "spring", damping: 25, stiffness: 300 }}
            className="fixed inset-x-4 top-1/4 -translate-y-1/2 z-50 max-w-sm mx-auto"
          >
            <div className="bg-white rounded-2xl p-6 shadow-2xl relative">
              {/* Botón cerrar */}
              <button
                onClick={onClose}
                className="absolute top-4 right-4 p-2 rounded-full hover:bg-gray-100 transition-colors"
              >
                <FaTimes className="w-4 h-4 text-gray-500" />
              </button>

              {/* Contenido */}
              <div className="text-center space-y-4">
                {/* Ícono de la app */}
                <div className="w-16 h-16 bg-gradient-to-br from-primary to-primary-hover rounded-2xl mx-auto flex items-center justify-center shadow-lg">
                  <span className="text-white font-bold text-xl">🎵</span>
                </div>

                {/* Título */}
                <h2 className="text-xl font-bold text-gray-900">
                  ¡Instala la App!
                </h2>

                {/* Descripción */}
                <p className="text-gray-600 text-sm leading-relaxed">
                  Para una mejor experiencia, añade esta app a tu pantalla de inicio.
                </p>

                {/* Pasos de instalación */}
                <div className="space-y-3 text-left">
                  <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                    <div className="w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center flex-shrink-0">
                      <span className="text-white text-xs font-bold">1</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-gray-700">
                      <span>Toca el botón</span>
                      <FaShare className="w-4 h-4 text-blue-500" />
                      <span>en la barra inferior</span>
                    </div>
                  </div>

                  <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                    <div className="w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center flex-shrink-0">
                      <span className="text-white text-xs font-bold">2</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-gray-700">
                      <span>Selecciona</span>
                      <div className="flex items-center gap-1 px-2 py-1 bg-white rounded border">
                        <FaPlus className="w-3 h-3" />
                        <span className="text-xs">Añadir a inicio</span>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                    <div className="w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center flex-shrink-0">
                      <span className="text-white text-xs font-bold">3</span>
                    </div>
                    <div className="text-sm text-gray-700">
                      <span>Confirma tocando</span>
                      <span className="font-semibold text-blue-600"> "Añadir"</span>
                    </div>
                  </div>
                </div>

                {/* Botones */}
                <div className="space-y-3 pt-2">
                  <button
                    onClick={onClose}
                    className="w-full py-3 bg-primary text-white font-semibold rounded-xl hover:bg-primary-hover transition-colors"
                  >
                    ¡Entendido!
                  </button>
                  
                  <button
                    onClick={onClose}
                    className="w-full py-2 text-gray-500 text-sm hover:text-gray-700 transition-colors"
                  >
                    Quizás más tarde
                  </button>
                </div>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};